import React from 'react'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, Shield, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { OverrideScope } from '@/types/calendar'

interface ClearOverridesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  scope: OverrideScope | null
  saving: boolean
  clearRestrictions: boolean
  onClearRestrictionsChange: (value: boolean) => void
  onConfirm: () => void
}

function longDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/**
 * Confirmation for removing existing price overrides.
 *
 * Deliberately NOT the review step. Review exists to show a shape the operator
 * chose — a percentage tracing the computed curve, a fixed price flattening it.
 * A clear has no chosen shape: every affected day goes back to whatever the
 * engine already says, which is the system's own answer and visible on the
 * calendar the moment the recompute lands.
 *
 * What genuinely needs deciding is smaller: how much is going, and whether the
 * restrictions riding on those same rows go with it.
 */
export default function ClearOverridesDialog({
  open,
  onOpenChange,
  scope,
  saving,
  clearRestrictions,
  onClearRestrictionsChange,
  onConfirm,
}: ClearOverridesDialogProps) {
  const total = scope?.total_overrides ?? 0
  const rooms = scope?.rooms ?? []
  const withRestrictions = scope?.with_restrictions ?? 0
  const singleDay = scope != null && scope.start_date === scope.end_date

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Clear manual prices?</DialogTitle>
          <DialogDescription>
            {scope
              ? singleDay
                ? longDate(scope.start_date)
                : `${longDate(scope.start_date)} – ${longDate(scope.end_date)}`
              : null}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <p className="text-sm text-foreground">
            <span className="font-semibold tabular-nums">{total}</span> manual{' '}
            {total === 1 ? 'price' : 'prices'} across{' '}
            <span className="font-semibold tabular-nums">{rooms.length}</span>{' '}
            {rooms.length === 1 ? 'room' : 'rooms'} will be removed. Those dates go back to the computed rate.
          </p>

          {rooms.length > 0 && (
            <ul className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-border/60 bg-muted/30 p-2">
              {rooms.map((r) => (
                <li key={r.room_type_id} className="flex items-center gap-2 px-1 text-xs">
                  <span className="min-w-0 flex-1 truncate text-foreground">{r.name}</span>
                  {r.with_restrictions > 0 && (
                    <Shield className="h-3 w-3 shrink-0 text-amber-500" aria-label="also has restrictions" />
                  )}
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {r.days} {r.days === 1 ? 'day' : 'days'}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {withRestrictions > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/40 px-3 py-2">
              <div className="min-w-0">
                <Label htmlFor="clear-restrictions-only" className="cursor-pointer text-xs font-medium">
                  Also clear restrictions
                </Label>
                <p className="text-xs text-muted-foreground">
                  <span className="tabular-nums">{withRestrictions}</span> of these also carry min stay, stop sell or
                  closed-to rules. They are kept unless you switch this on.
                </p>
              </div>
              <Switch
                id="clear-restrictions-only"
                checked={clearRestrictions}
                onCheckedChange={onClearRestrictionsChange}
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            className={cn('rounded-lg bg-rose-600 hover:bg-rose-700')}
            onClick={onConfirm}
            disabled={saving || total === 0}
          >
            {saving ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            )}
            Clear {total} {total === 1 ? 'price' : 'prices'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
